/**
 * Session Manager Utility
 * Prevents multiple tabs from being open when user is logged in
 */

const SESSION_KEY = 'busman_active_session'
const SESSION_CHANNEL = 'busman_session_channel'

export type SessionRecord = {
  tabId: string
  timestamp: number
}

/** Parse the tab-session record. Invalid JSON must not throw — that white-screens refresh. */
export function parseSessionRecord(raw: string | null): SessionRecord | null {
  if (!raw) {
    return null
  }

  try {
    const sessionData = JSON.parse(raw)
    if (
      !sessionData ||
      typeof sessionData !== 'object' ||
      typeof sessionData.tabId !== 'string' ||
      typeof sessionData.timestamp !== 'number'
    ) {
      return null
    }
    return { tabId: sessionData.tabId, timestamp: sessionData.timestamp }
  } catch {
    return null
  }
}

function readStoredSession(): SessionRecord | null {
  const raw = localStorage.getItem(SESSION_KEY)
  if (raw == null) {
    return null
  }

  const sessionData = parseSessionRecord(raw)
  if (!sessionData) {
    localStorage.removeItem(SESSION_KEY)
    return null
  }

  return sessionData
}

export class SessionManager {
  private broadcastChannel: BroadcastChannel | null = null
  private isActiveSession: boolean = false

  // Heartbeat mechanism: The active tab updates a timestamp in localStorage every 5 seconds to indicate it's alive.
  // Stale session detection: If the last heartbeat is older than 15 seconds, the session is considered stale (original tab likely closed) and is cleared.
  // Automatic session takeover: When a new tab detects a stale session:
  // It clears the stale session
  // Allows the user to proceed without blocking
  private heartbeatInterval: number | null = null
  private readonly HEARTBEAT_INTERVAL = 5000 // 5 seconds
  private readonly HEARTBEAT_TIMEOUT = 15000 // 15 seconds - consider session dead if no heartbeat

  constructor() {
    // Initialize BroadcastChannel for cross-tab communication
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel(SESSION_CHANNEL)
      this.setupChannelListeners()
    }

    // Setup page visibility and unload handlers
    this.setupPageHandlers()

    // Check if there's already an active session
    this.checkExistingSession()
  }

  /**
   * Setup listeners for cross-tab communication
   */
  private setupChannelListeners(): void {
    if (!this.broadcastChannel) return

    this.broadcastChannel.onmessage = (event) => {
      const { type, tabId } = event.data

      if (type === 'session_check') {
        // Another tab is checking for active session
        if (this.isActiveSession) {
          this.broadcastChannel?.postMessage({
            type: 'session_exists',
            tabId: this.getTabId(),
          })
        }
      } else if (type === 'session_exists') {
        // Another tab has an active session
        if (tabId !== this.getTabId()) {
          this.handleExistingSession()
        }
      } else if (type === 'session_started') {
        // Another tab just started a session
        if (tabId !== this.getTabId() && !this.isActiveSession) {
          this.handleExistingSession()
        }
      } else if (type === 'session_ended') {
        // Another tab ended the session
        // This tab can now allow login if needed
      }
    }

    // Listen for storage events (fallback for browsers without BroadcastChannel)
    window.addEventListener('storage', (event) => {
      if (event.key === SESSION_KEY) {
        if (event.newValue && !this.isActiveSession) {
          // Another tab set the session
          this.handleExistingSession()
        } else if (!event.newValue && this.isActiveSession) {
          // Another tab cleared the session
          this.isActiveSession = false
        }
      }
    })
  }

  /**
   * Setup page visibility and unload handlers
   */
  private setupPageHandlers(): void {
    // Handle page visibility changes (tab switching, minimizing, etc.)
    document.addEventListener('visibilitychange', () => {
      if (this.isActiveSession) {
        if (document.hidden) {
          // Tab is hidden, but keep heartbeat running
          // This allows the session to be detected as active
        } else {
          // Tab is visible again, ensure heartbeat is running
          this.startHeartbeat()
        }
      } else {
        // Tab is visible but doesn't have active session
        // Check if there's a stale session we can take over
        this.checkForStaleSession()
      }
    })

    // Handle page unload (tab closing)
    window.addEventListener('beforeunload', () => {
      if (this.isActiveSession) {
        // Clear the session when tab closes
        this.endSession()
      }
    })
  }

  /**
   * Check for stale sessions that can be taken over
   */
  private checkForStaleSession(): void {
    const sessionData = readStoredSession()
    if (!sessionData) return

    const currentTabId = this.getTabId()

    // If it's already our session, nothing to do
    if (sessionData.tabId === currentTabId) return

    // Check if heartbeat is stale
    const now = Date.now()
    const timeSinceLastHeartbeat = now - sessionData.timestamp

    if (timeSinceLastHeartbeat > this.HEARTBEAT_TIMEOUT) {
      // Heartbeat is stale, original tab is likely closed
      // Clear the stale session so this tab can proceed
      this.clearSession()
    }
  }

  /**
   * Start the heartbeat mechanism to keep session alive
   */
  private startHeartbeat(): void {
    if (this.heartbeatInterval) {
      return // Already running
    }

    this.heartbeatInterval = window.setInterval(() => {
      if (this.isActiveSession) {
        const sessionData = {
          tabId: this.getTabId(),
          timestamp: Date.now(),
        }
        localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData))
      }
    }, this.HEARTBEAT_INTERVAL)
  }

  /**
   * Stop the heartbeat mechanism
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  /**
   * Check if there's already an active session in another tab
   */
  private checkExistingSession(): void {
    const sessionData = readStoredSession()

    if (sessionData) {
      const tabId = sessionData.tabId
      const timestamp = sessionData.timestamp

      // Check if session is still valid (not older than 1 hour)
      const now = Date.now()
      const oneHour = 60 * 60 * 1000

      if (now - timestamp < oneHour) {
        const currentTabId = this.getTabId()
        // Check if this is the same tab (same tab ID) or a different tab
        if (tabId === currentTabId) {
          // This is the same tab (e.g., page refresh), maintain session
          this.isActiveSession = true
          // Update timestamp to keep session alive
          this.startSession()
          return
        } else {
          // Check if the heartbeat is stale (original tab might be closed)
          const timeSinceLastHeartbeat = now - timestamp
          if (timeSinceLastHeartbeat > this.HEARTBEAT_TIMEOUT) {
            // Heartbeat is stale, original tab is likely closed
            // Allow this tab to take over the session
            this.clearSession()
            // Continue to allow this tab to proceed
          } else {
            // Another tab has an active session with recent heartbeat
            this.handleExistingSession()
            return
          }
        }
      } else {
        // Session expired, clear it
        this.clearSession()
      }
    }

    // Broadcast to check if any other tab has an active session
    this.broadcastChannel?.postMessage({
      type: 'session_check',
      tabId: this.getTabId(),
    })
  }

  /**
   * Handle the case when another tab already has an active session
   */
  private handleExistingSession(): void {
    // Show a message to the user
    const message = 'You are already logged in another tab. Please close this tab or use the existing session.'

    // Dispatch a custom event that the app can listen to
    window.dispatchEvent(
      new CustomEvent('session-conflict', {
        detail: { message },
      }),
    )

    // Optionally, you can redirect to login page
    // This will be handled by the router guard
  }

  /**
   * Start a new session (call this when user logs in)
   */
  public startSession(): void {
    const tabId = this.getTabId()
    const sessionData = {
      tabId,
      timestamp: Date.now(),
    }

    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData))
    this.isActiveSession = true

    // Start heartbeat to keep session alive
    this.startHeartbeat()

    // Broadcast to other tabs that a session has started
    this.broadcastChannel?.postMessage({
      type: 'session_started',
      tabId,
    })
  }

  /**
   * End the current session (call this when user logs out)
   */
  public endSession(): void {
    const tabId = this.getTabId()
    localStorage.removeItem(SESSION_KEY)
    this.isActiveSession = false

    // Stop heartbeat
    this.stopHeartbeat()

    // Broadcast to other tabs that the session has ended
    this.broadcastChannel?.postMessage({
      type: 'session_ended',
      tabId,
    })
  }

  /**
   * Check if this tab has an active session
   */
  public hasActiveSession(): boolean {
    return this.isActiveSession
  }

  /**
   * Check if another tab has an active session
   */
  public hasOtherTabSession(): boolean {
    const sessionData = readStoredSession()
    if (!sessionData) return false

    const currentTabId = this.getTabId()

    // If it's the same tab, no conflict
    if (sessionData.tabId === currentTabId) return false

    // Check if heartbeat is stale (original tab might be closed)
    const now = Date.now()
    const timeSinceLastHeartbeat = now - sessionData.timestamp

    if (timeSinceLastHeartbeat > this.HEARTBEAT_TIMEOUT) {
      // Heartbeat is stale, original tab is likely closed
      // Clear the stale session
      this.clearSession()
      return false
    }

    return true
  }

  /**
   * Get a unique ID for this tab
   */
  private getTabId(): string {
    let tabId = sessionStorage.getItem('tab_id')
    if (!tabId) {
      tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      sessionStorage.setItem('tab_id', tabId)
    }
    return tabId
  }

  /**
   * Clear the session (cleanup)
   */
  private clearSession(): void {
    localStorage.removeItem(SESSION_KEY)
    this.isActiveSession = false
    this.stopHeartbeat()
  }

  /**
   * Cleanup when the manager is destroyed
   */
  public destroy(): void {
    this.stopHeartbeat()
    if (this.isActiveSession) {
      this.endSession()
    }
    this.broadcastChannel?.close()
    this.broadcastChannel = null
  }
}

// Create a singleton instance
let sessionManagerInstance: SessionManager | null = null

export function getSessionManager(): SessionManager {
  if (!sessionManagerInstance) {
    sessionManagerInstance = new SessionManager()
  }
  return sessionManagerInstance
}
